import { QuotaType } from '@bpinternal/const'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { ArrowRight, Minus, Plus } from 'lucide-react'
import { FC } from 'react'
import { Badge } from '~/elements'
import { ProductIcon } from '~/features/billing/components'
import { cn, formatters } from '~/utils'
import { AddonDetails, AddonQuotaType } from '../types/index'

type AddonsProps = {
  addons: AddonDetails[]
  addonDeltas: Record<AddonQuotaType, number>
  updateAddonDelta: (addonQuotaName: AddonQuotaType, delta: number) => void
}

export const AddonItems = ({ addons, addonDeltas, updateAddonDelta }: AddonsProps) => {
  return (
    <div>
      {/* Mobile View */}
      <div className="flex flex-col gap-4 md:hidden">
        {addons.map((addon) => (
          <AddonCards key={addon.productId} addon={addon} addonDeltas={addonDeltas} onUpdate={updateAddonDelta} />
        ))}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block">
        <Table>
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow>
              <TableHead>Feature</TableHead>
              <TableHead className="text-center">Quantity</TableHead>
              <TableHead className="text-center">Subtotal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {addons.map((addon) => (
              <AddonTableRow
                key={addon.productId}
                addon={addon}
                addonDeltas={addonDeltas}
                onUpdate={updateAddonDelta}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const AddonCards: FC<{
  addon: AddonDetails
  addonDeltas: Record<AddonQuotaType, number>
  onUpdate: (addonQuotaName: AddonQuotaType, delta: number) => void
}> = ({ addon, addonDeltas, onUpdate }) => {
  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-neutral-50 p-4">
      <div className="flex justify-between">
        <div className="flex gap-3">
          <ProductIcon
            productQuotaName={addon.quotaName as QuotaType}
            className="h-8 w-8 rounded-lg bg-blue-400 p-1.5 text-white"
          />
          <div>
            <span className="text-sm">{addon.displayName}</span>
            <p className="text-xs text-muted-foreground">{addon.description}</p>
          </div>
        </div>
        <div className="font-semibold text-muted-foreground">
          {formatters.$centdollars(
            addon.priceCents * (addon.nextPaymentQuantity + addonDeltas[addon.quotaName] || 0),
            0
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">Quantity</div>
        <AddonQuantityControls addon={addon} onUpdate={onUpdate} addonDeltas={addonDeltas} />
      </div>
    </div>
  )
}

const AddonTableRow: FC<{
  addon: AddonDetails
  addonDeltas: Record<AddonQuotaType, number>
  onUpdate: (addonQuotaName: AddonQuotaType, delta: number) => void
}> = ({ addon, addonDeltas, onUpdate }) => {
  const subtotal = (addon.nextPaymentQuantity + addonDeltas[addon.quotaName]) * addon.priceCents

  return (
    <TableRow className="border-b-0">
      <TableCell>
        <div className="flex items-start gap-3">
          <ProductIcon
            className="h-9 w-9 rounded-lg bg-blue-400 p-1.5 text-white"
            productQuotaName={addon.quotaName as QuotaType}
          />
          <div>
            <p className="font-medium">{addon.displayName}</p>
            <p className="text-sm text-muted-foreground">{addon.description}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="w-40">
        <AddonQuantityControls addon={addon} addonDeltas={addonDeltas} onUpdate={onUpdate} />
      </TableCell>
      <TableCell className="w-24">
        <div className="flex flex-col gap-1">
          <span className={cn('text-center text-sm font-normal', subtotal === 0 && 'text-muted-foreground')}>
            {formatters.$centdollars(subtotal, 0)}
          </span>
        </div>
      </TableCell>
    </TableRow>
  )
}

const AddonQuantityControls: FC<{
  addon: AddonDetails
  addonDeltas: Record<AddonQuotaType, number>
  onUpdate: (addonQuotaName: AddonQuotaType, delta: number) => void
}> = ({ addon, addonDeltas, onUpdate }) => {
  const hasChanges = addon.nextPaymentQuantity + addonDeltas[addon.quotaName] !== addon.currentQuantity
  const isImmediate = addon.nextPaymentQuantity + addonDeltas[addon.quotaName] > addon.currentQuantity

  return (
    <div className="flex w-36 items-center justify-between">
      <Button
        variant="secondary"
        className="my-0.5"
        size="icon"
        onClick={() => onUpdate(addon.quotaName, -1)}
        disabled={addon.nextPaymentQuantity + addonDeltas[addon.quotaName] === 0}
        aria-label={`Decrease ${addon.name} quantity`}
      >
        <Minus className="h-5 w-5" />
      </Button>
      <div className="text-center">
        {hasChanges ? (
          <div className="flex items-center justify-center gap-1">
            <span className="text-muted-foreground">{addon.currentQuantity}</span>
            <ArrowRight className="h-3 w-3 stroke-neutral-400 !stroke-[2.5px]" />
            <span>{addon.nextPaymentQuantity + addonDeltas[addon.quotaName]}</span>
          </div>
        ) : (
          <span>{addon.nextPaymentQuantity + addonDeltas[addon.quotaName]}</span>
        )}
        {hasChanges && (
          <div className="text-xs text-muted-foreground">
            {isImmediate ? (
              'Immediate'
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">Next Month</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>The current limit will remain active until the next billing cycle.</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <Button
        variant="secondary"
        size="icon"
        className="my-0.5"
        onClick={() => onUpdate(addon.quotaName, 1)}
        aria-label={`Increase ${addon.name} quantity`}
      >
        <Plus className="h-5 w-5" />
      </Button>
    </div>
  )
}
